/**
 * AnalyticsHeader Component
 * 
 * Header section with tabs and summary statistics.
 */

import React from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
} from "@mui/material";

export interface AnalyticsHeaderProps {
  selectedTab: "expenses" | "incomes" | "adjustments";
  onTabChange: (newValue: "expenses" | "incomes" | "adjustments") => void;
  totalTransactions: number;
  totalAmount: number;
  formatAmount: (amount: number) => string;
  onNavigateToActivity?: (filters?: {
    category?: string;
    account?: string;
    users?: string[];
    startDate?: string;
    endDate?: string;
    typeFilter?: string;
  }) => void;
  selectedAccounts: string[];
  selectedUsers: string[];
  workspaceMembersLength: number;
  fromDate: string;
  toDate: string;
  expensesAndIncomesAmounts?: {
    expensesAmount: number;
    incomesAmount: number;
  };
}

/**
 * Analytics header with tabs and summary stats
 */
export const AnalyticsHeader: React.FC<AnalyticsHeaderProps> = ({
  selectedTab,
  onTabChange,
  totalTransactions,
  totalAmount,
  formatAmount,
  onNavigateToActivity,
  selectedAccounts,
  selectedUsers,
  workspaceMembersLength,
  fromDate,
  toDate,
  expensesAndIncomesAmounts,
}) => {
  return (
    <>
      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3, position: "relative" }}>
        <Tabs
          value={selectedTab}
          onChange={(e, newValue) => {
            onTabChange(newValue);
          }}
          aria-label="analytics tabs"
          sx={{
            "& .MuiTabs-flexContainer": {
              justifyContent: "space-between",
            },
            "& .MuiTab-root:nth-of-type(1)": {
              // Expenses - add spacing after to separate from Incomes
              marginRight: 2,
            },
            "& .MuiTab-root:nth-of-type(2)": {
              // Incomes - add gap before Adjustments
              marginRight: "auto",
            },
            "& .MuiTab-root:nth-of-type(3)": {
              // Adjustments - separate on the right
              marginLeft: "auto",
              borderLeft: "1px solid",
              borderColor: "divider",
              paddingLeft: 2,
            },
          }}
        >
          <Tab 
            label={
              expensesAndIncomesAmounts ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Typography variant="body2" fontWeight="bold">
                    Expenses
                  </Typography>
                  <Typography variant="caption" fontWeight="medium" sx={{ fontSize: "0.75rem", opacity: 0.85, color: "text.secondary" }}>
                    {formatAmount(expensesAndIncomesAmounts.expensesAmount)}
                  </Typography>
                </Box>
              ) : (
                "Expenses"
              )
            }
            value="expenses" 
          />
          <Tab 
            label={
              expensesAndIncomesAmounts ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Typography variant="body2" fontWeight="bold">
                    Incomes
                  </Typography>
                  <Typography variant="caption" fontWeight="medium" sx={{ fontSize: "0.75rem", opacity: 0.85, color: "text.secondary" }}>
                    {formatAmount(expensesAndIncomesAmounts.incomesAmount)}
                  </Typography>
                </Box>
              ) : (
                "Incomes"
              )
            }
            value="incomes" 
          />
          <Tab label="Adjustments" value="adjustments" />
        </Tabs>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="h6" fontWeight="medium">
            {selectedTab === "expenses" && "Expenses"}
            {selectedTab === "incomes" && "Incomes"}
            {selectedTab === "adjustments" && "Adjustments"}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 3, textAlign: "right" }}>
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {selectedTab === "expenses" && "Total Transactions"}
              {selectedTab === "incomes" && "Total Transactions"}
              {selectedTab === "adjustments" && "Total Adjustments"}
            </Typography>
            <Typography
              variant="h6"
              fontWeight="semibold"
              component="button"
              onClick={() => {
                if (!onNavigateToActivity) return;
                
                // Prepare filters based on current Analytics state
                const filters: {
                  category?: string;
                  account?: string;
                  users?: string[];
                  startDate?: string;
                  endDate?: string;
                  typeFilter?: string;
                } = {};
                
                // Set type filter based on selected tab
                if (selectedTab === "expenses") {
                  filters.typeFilter = "Expenses";
                } else if (selectedTab === "incomes") {
                  filters.typeFilter = "Income";
                } else if (selectedTab === "adjustments") {
                  filters.typeFilter = "Adjustment";
                }
                
                // Set account if one is selected (Activity page currently supports single account)
                if (selectedAccounts.length === 1) {
                  filters.account = selectedAccounts[0];
                }
                
                // Set users if any are selected
                if (selectedUsers.length > 0 && selectedUsers.length < workspaceMembersLength) {
                  filters.users = selectedUsers;
                }
                
                // Set date range
                if (fromDate) filters.startDate = fromDate;
                if (toDate) filters.endDate = toDate;
                
                onNavigateToActivity(filters);
              }}
              sx={{
                textDecoration: "none",
                color: "primary.main",
                cursor: "pointer",
                "&:hover": {
                  textDecoration: "underline",
                },
                border: "none",
                background: "none",
                padding: 0,
                font: "inherit",
              }}
            >
              {totalTransactions}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {selectedTab === "expenses" && "Total Expenses"}
              {selectedTab === "incomes" && "Total Income"}
              {selectedTab === "adjustments" && "Total Adjustments"}
            </Typography>
            <Typography variant="h6" fontWeight="semibold">
              {formatAmount(totalAmount)}
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
};
